import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    CircularProgress,
    IconButton,
    Divider,
} from "@mui/material";
import { X, Search, Briefcase } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import jobPostingApi from "../../api/jobPosting";

const AttachJobModal = ({ open, onClose, onSelectJob }) => {
    const [search, setSearch] = useState("");

    const { data, isLoading, isError } = useQuery({
        queryKey: ["myJobPostings"],
        queryFn: () => jobPostingApi.getMyJobPostings(),
        enabled: open,
    });

    // Handle both direct data and axios response objects
    const responseData = data?.data || data;
    const jobs = responseData?.results || (Array.isArray(responseData) ? responseData : []);

    const filtered = jobs.filter(job => 
        job?.title?.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (job) => {
        const jobUrl = `${window.location.origin}/job-details/${job.id}`;
        const jobLink = `[${job.title}](${jobUrl})`;
        onSelectJob(jobLink);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: { borderRadius: 3, overflow: "hidden" },
            }}
        >
            <DialogTitle
                sx={{
                    background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    px: 2,
                }}
            >
                <div className="flex items-center gap-2">
                    <Briefcase size={20} />
                    <span className="font-semibold text-base">Attach a Job</span>
                </div>
                <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                    <X size={18} />
                </IconButton>
            </DialogTitle>

            <div className="px-4 pt-3 pb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full bg-gray-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <Search size={15} className="text-gray-400 shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search your jobs..."
                        className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400"
                    />
                </div>
            </div>

            <Divider />

            <DialogContent sx={{ p: 0, maxHeight: 400, overflowY: "auto" }}>
                {isLoading && (
                    <div className="flex justify-center py-8">
                        <CircularProgress size={28} />
                    </div>
                )}
                {isError && (
                    <Typography className="text-center p-6 text-red-500" variant="body2">
                        Could not load your job postings.
                    </Typography>
                )}
                {!isLoading && !isError && filtered.length === 0 && (
                    <div className="flex flex-col items-center py-10 gap-2 text-gray-400">
                        <Briefcase size={32} className="opacity-40" />
                        <Typography variant="body2">No active jobs found</Typography>
                    </div>
                )}
                {!isLoading && !isError && filtered.map((job) => (
                    <button
                        key={job.id}
                        onClick={() => handleSelect(job)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-blue-50 active:bg-blue-100 transition-colors text-left border-b border-gray-100 last:border-0"
                    >
                        <div className="flex flex-col min-w-0 flex-1">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }} className="truncate">
                                {job.title}
                            </Typography>
                            <Typography variant="caption" className="text-gray-500 truncate mt-0.5">
                                {job.location || 'Remote'} {job.employment_type ? `• ${job.employment_type}` : ''}
                            </Typography>
                        </div>
                        <span className="text-[11px] font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded-full shrink-0">
                            Select
                        </span>
                    </button>
                ))}
            </DialogContent>
        </Dialog>
    );
};

export default AttachJobModal;
